/**
 * Record 필드 값 검증 중앙 함수 (v2.7)
 *
 * 기존 Record 생성 시 `pages/records/components/helper.js:getRulesByType`이 사용하는
 * 검증 규칙과 동일한 규칙을 순수 함수 형태로 제공한다.
 * 현재 Import v2 validator가 이 함수를 호출해 동일한 검증을 수행.
 * 후속 작업에서 helper.js도 이 함수를 호출하도록 통합 예정 (DRY).
 */

import { normalizeToOtpauthUri } from 'services/totp'

const PASSWORD_PATTERN = /^[a-zA-Z0-9!@#$%^&*()_+~`|₩"}{[\]:;'<>,.?\\/=\-]*$/
const NUMBER_PATTERN = /^[0-9]*$/
const URL_PATTERN =
  /^(https?:\/\/)?[-a-zA-Z0-9가-힣ㄱ-ㅎㅏ-ㅣ@:%._+~#=]{1,256}\.[a-zA-Z0-9가-힣ㄱ-ㅎㅏ-ㅣ()]{1,6}\b([-a-zA-Z0-9가-힣ㄱ-ㅎㅏ-ㅣ()@:%_+.~#?&//=]*)$/
// 이메일은 ASCII (영문 + 숫자 + 일반 특수문자) 만 허용. 한글/일본어/중국어 등 비영문은 차단.
// local part: 영숫자 + . _ % + - / domain: 영숫자 + . - / TLD: 영문 2자 이상.
export const EMAIL_PATTERN = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

/**
 * 단일 필드 값 검증.
 *
 * @param {string} uiType - FIELD_TYPES 값 ('text'|'secure_text'|'email'|'secure_email'|'password'|'pin'|'url'|'note'|'secure_note'|'date')
 * @param {string} value - 검증 대상 값 (trim 전)
 * @param {Object} [opts]
 * @param {boolean} [opts.required=false] - 필수 여부
 * @param {number|null} [opts.minLength=null] - 최소 길이 (> 0일 때만 적용)
 * @param {number|null} [opts.maxLength=null] - 최대 길이 (> 0일 때만 적용)
 * @returns {{ valid: boolean, reason: string|null, messageKey: string|null, messageParams: object }}
 */
export const validateFieldValue = (uiType, value, opts = {}) => {
  const v = (value || '').toString().trim()
  const { required = false, minLength = null, maxLength = null } = opts

  // 1. 필수값
  if (required && !v) {
    return {
      valid: false,
      reason: 'requiredEmpty',
      messageKey: 'validation.required.invalid',
      messageParams: {},
    }
  }

  // 선택 필드 + 빈값 → 통과 (나머지 패턴/길이 검증 skip)
  if (!v) {
    return { valid: true, reason: null, messageKey: null, messageParams: {} }
  }

  // 1.5 totp(PR10): base32 secret 또는 otpauth URI 만 허용 — 깨진 인증 키가 조용히
  // 저장되는 것을 막는다(normalizeToOtpauthUri 가 저장 경로와 동일 판정 — URI 는 parseTotp,
  // bare 는 toSecret 경유로 throw).
  if (uiType === 'totp') {
    try {
      normalizeToOtpauthUri(v)
    } catch (e) {
      // 미지원 방식(HOTP·Steam)은 키가 틀린 게 아니라 우리가 못 다루는 것 — "올바른 키가 아니다"
      // 로 뭉개면 사용자가 있지도 않은 오타를 찾는다.
      const unsupported = e?.code === 'UNSUPPORTED_TOTP'
      return {
        valid: false,
        reason: unsupported ? 'totpUnsupported' : 'totpInvalid',
        messageKey: unsupported ? 'record.totp.unsupported' : 'validation.totp.invalid',
        messageParams: {},
      }
    }
    return { valid: true, reason: null, messageKey: null, messageParams: {} }
  }

  // 2. 비밀번호 타입 문자 허용 범위 (password만. secure_text는 암호화 여부일 뿐 내용은 일반 텍스트 허용)
  if (uiType === 'password') {
    if (!PASSWORD_PATTERN.test(v)) {
      return {
        valid: false,
        reason: 'pwtype',
        messageKey: 'validation.pwtype.invalid',
        messageParams: {},
      }
    }
  }

  // 3. Pincode 숫자만
  if (uiType === 'pin') {
    if (!NUMBER_PATTERN.test(v)) {
      return {
        valid: false,
        reason: 'numberOnly',
        messageKey: 'validation.number.invalid',
        messageParams: {},
      }
    }
  }

  // 4. URL 도메인 형식 (https:// 자동 prepend 후 검증 — 호출자는 원본 값 전달)
  if (uiType === 'url') {
    const normalized = /^https?:\/\//i.test(v) ? v : `https://${v}`
    if (!URL_PATTERN.test(normalized)) {
      return {
        valid: false,
        reason: 'urlFormat',
        messageKey: 'validation.url.invalid',
        messageParams: {},
      }
    }
  }

  // 5. 날짜 형식 YYYY-MM-DD (validation.date.invalid 키 미존재로 기존 import v2 키 재사용)
  if (uiType === 'date') {
    if (!DATE_PATTERN.test(v)) {
      return {
        valid: false,
        reason: 'dateFormat',
        messageKey: 'record.import.v2.validation.error.formatError',
        messageParams: {},
      }
    }
  }

  // 6. 이메일 형식
  if (uiType === 'email' || uiType === 'secure_email') {
    if (!EMAIL_PATTERN.test(v)) {
      return {
        valid: false,
        reason: 'emailFormat',
        messageKey: 'validation.email.invalid',
        messageParams: {},
      }
    }
  }

  // 7. 최소 길이 (minLength > 0일 때만)
  if (minLength != null && minLength > 0 && v.length < minLength) {
    return {
      valid: false,
      reason: 'minLength',
      messageKey: 'validation.minLength.invalid',
      messageParams: { length: minLength },
    }
  }

  // 8. 최대 길이 (maxLength > 0일 때만)
  if (maxLength != null && maxLength > 0 && v.length > maxLength) {
    return {
      valid: false,
      reason: 'maxLength',
      messageKey: 'validation.maxLength.invalid',
      messageParams: { length: maxLength },
    }
  }

  return { valid: true, reason: null, messageKey: null, messageParams: {} }
}

export default validateFieldValue
